import React, { useState, useEffect, FormEvent, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";

import styles from './styles/SkillDetailsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Skill {
  skill_id: number;
  name: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  skill_id?: string;
}

const SkillDetailsPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { skill_id } = useParams<RouteParams>();
  const [name, setName] = useState<string>("");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);

  // Track page visit once we have the skill name
  useEffect(() => {
    if (name && !hasTrackedRef.current) {
      trackPageVisit(
        skill_id ? `Skill: ${name}` : "New Skill",
        skill_id ? `/skills/${skill_id}` : "/skills/create"
      );
      hasTrackedRef.current = true;
    }
  }, [name, skill_id]);

  useEffect(() => {
    if (skill_id) {
      const fetchSkill = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/skills/${skill_id}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Org-ID": selectedOrgID?.toString() || "",
            },
          });

          if (!response.ok) {
            const errorData = await response.json();
            setError(errorData.error || "Failed to fetch skill.");
            return;
          }

          const data: Skill = await response.json();
          if (data) {
            setName(data.name || "");
          } else {
            setError("No data received for the skill.");
          }
        } catch (err) {
          setError("An unexpected error occurred.");
        }
      };

      fetchSkill();
    }
  }, [skill_id, token, selectedOrgID]);

  const handleSaveOrUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Skill name cannot be empty.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/skills${skill_id ? `/${skill_id}` : ""}`,
        {
          method: skill_id ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
          body: JSON.stringify({
            name,
            org_id: selectedOrgID ? parseInt(selectedOrgID.toString(), 10) : 0,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save skill.");
        return;
      }

      navigate("/skills");
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{skill_id ? "Edit Skill" : "New Skill"}</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      <form onSubmit={handleSaveOrUpdate} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Skill Name:</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={styles.input}
          />
        </div>
        <button type="submit" className={styles.submitButton}>
          {skill_id ? "Update" : "Save"}
        </button>
      </form>
    </div>
  );
};

export default SkillDetailsPage;
